import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Pencil,
  Archive,
  ArchiveRestore,
  Plus,
  Bookmark,
  BookmarkCheck,
  ChevronDown,
  Star,
} from "lucide-react";
import type { RouterOutputs } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { importanceToneClass } from "@/lib/importance";
import { MyWorkPanel } from "./MyWorkPanel";

/**
 * Stage 2A + Step B — Team Direction card (presentational, thin-client).
 *
 * Renders the SHARED team-priority list and a PRIVATE self-link toggle. It is
 * pure props + callbacks (no data fetching) and is the source of truth for the
 * Stage-2A privacy UX:
 *   - shows ONLY server-safe fields (no createdByUserId — the type is the
 *     sanitized server output; authorship surfaces only as `youAuthored`);
 *   - the link toggle reflects ONLY the current user's own `linkedIds`; no other
 *     user's link state, no link COUNT, no "X of Y", no momentum, ever;
 *   - linking is framed as private self-organization, never compliance — a
 *     not-linked item shows an invitation, never a warning (no silence-as-signal).
 *
 * Step B — archive-only lifecycle. Active priorities and archived priorities are
 * split into two sections; the archived section is separated and collapsed
 * behind a neutral toggle (all members may VIEW it). Authorized roles
 * (owner/manager/lead, via `canEdit`) get an Unarchive control on archived rows.
 * There is NO hard delete anywhere — archive preserves the record. Archive and
 * unarchive are link-AGNOSTIC: this card never reads/counts/branches on a row's
 * private links to decide what archive/unarchive shows or does (archived rows
 * carry no link UI at all), so there is no link-existence or count oracle.
 */
export type TeamDirectionRow = RouterOutputs["teamPriorities"]["list"][number];

/* ---- Phase W2 — per-priority weekly declarations (human-owned status) ---- */

export type DeclarationWire =
  RouterOutputs["teamPriorityWeeklyDeclarations"]["list"][number];

export type DeclarationStatus =
  | "on_track"
  | "needs_attention"
  | "blocked"
  | "completed_this_week";

export type DeclarationValues = {
  status: DeclarationStatus;
  note: string | null;
  blocker: string | null;
  nextStep: string | null;
  decisionRequired: string | null;
};

const DECL_STATUS_LABEL: Record<DeclarationStatus, [string, string]> = {
  on_track: ["On track", "على المسار"],
  needs_attention: ["Needs attention", "يحتاج انتباهًا"],
  blocked: ["Blocked", "معطَّل"],
  completed_this_week: ["Completed this week", "أُنجز هذا الأسبوع"],
};

const DECL_STATUS_TONE: Record<DeclarationStatus, string> = {
  on_track: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  needs_attention: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  // §11 — `blocked` MUST NOT reuse the focus marker's coral. The marker below
  // renders `bg-coral-soft/70 text-coral`, so a focused + blocked row used to
  // show two byte-identical coral pills side by side: emphasis and progress
  // read as one badge, which is exactly what the two-badge design prevents.
  // Orange is what TeamReport.tsx and WeeklyTeamUpdateCard.tsx already use for
  // `blocked`, so this also makes the three surfaces agree.
  blocked: "bg-orange-500/15 text-orange-800 dark:text-orange-300",
  completed_this_week: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
};

/**
 * The focus marker's tone, kept as a named constant so the contract test can
 * assert it differs from EVERY declaration status tone. Focus = emphasis
 * ("this matters this week"); status = declared progress. They must never be
 * mistakable for one another.
 */
export const FOCUS_MARKER_TONE = "bg-coral-soft/70 text-coral";

const impLabel = (imp: number, ar: boolean) =>
  (ar ? ["", "عالٍ", "متوسط", "منخفض"] : ["", "High", "Medium", "Low"])[imp] ?? "";

const statusLabel = (s: TeamDirectionRow["teamStatus"], ar: boolean) =>
  ({
    active: ar ? "نشِط" : "Active",
    archived: ar ? "مؤرشَف" : "Archived",
    done: ar ? "منجز" : "Done",
  })[s];

export interface TeamDirectionCardProps {
  priorities: TeamDirectionRow[];
  /** The CURRENT user's own linked priority ids (from priorityLinks.mine). */
  linkedIds: Set<number>;
  /** Role hint for create/edit/archive/unarchive affordances. Server stays authority. */
  canEdit: boolean;
  isAr: boolean;
  /**
   * Stage 1 (weekly operating model) — the VIEWED week. Team Direction is
   * persistent (rows may be from any created-in `period`), so personal work
   * opened from a direction files into the week the owner is CURRENTLY viewing,
   * never the row's own `period`. Fed straight into each MyWorkPanel.
   */
  weekStartKey: string;
  busyLinkId?: number | null;
  onToggleLink: (priorityId: number, currentlyLinked: boolean) => void;
  /**
   * Stage 2 — Weekly Focus (shared, week-specific). `focusedIds` are the team
   * directions marked focus for the VIEWED week; `focusNoteById` carries each
   * one's optional shared note. Both derive ONLY from shared weeklyFocus data —
   * never from private links/relevance/receipts. The marker is visible to all;
   * the toggle + note editor render only when `canEdit`.
   */
  focusedIds: Set<number>;
  focusNoteById: Map<number, string | null>;
  busyFocusId?: number | null;
  onToggleFocus: (teamPriorityId: number, currentlyFocused: boolean) => void;
  onSetFocusNote: (teamPriorityId: number, note: string) => void;
  /**
   * Phase W1 — when true, Weekly Focus is READ-ONLY on the direction card
   * (it is now SET in the Weekly Report wizard Step 1). Show only the
   * "focused this week" marker + any shared note; hide the mark/unmark
   * toggle, the note editor, and the "no focus set" prompt.
   */
  focusReadOnly?: boolean;
  /**
   * Phase W2 — when true, the per-priority Declaration is READ-ONLY on the
   * card (declarations are DECLARED in the Weekly Report wizard Step 2). Show
   * only the shared status badge + text; hide the Add/Edit affordance.
   */
  declarationsReadOnly?: boolean;
  /**
   * Phase W2 — per-priority weekly declarations for the VIEWED week. Shared
   * team data (all members see saved declarations); the editor renders only
   * for authority. Status is HUMAN-owned: the editor starts unset, nothing is
   * preselected, and no machine ever writes it.
   */
  declarationByPriorityId: Map<number, DeclarationWire>;
  declBusy?: boolean;
  onSaveDeclaration: (teamPriorityId: number, values: DeclarationValues) => void;
  onClearDeclaration: (teamPriorityId: number) => void;
  onCreate: () => void;
  onEdit: (priority: TeamDirectionRow) => void;
  onArchive: (priorityId: number) => void;
  onUnarchive: (priorityId: number) => void;
}

export function TeamDirectionCard({
  priorities,
  linkedIds,
  canEdit,
  isAr,
  weekStartKey,
  busyLinkId,
  onToggleLink,
  focusedIds,
  focusNoteById,
  busyFocusId,
  onToggleFocus,
  onSetFocusNote,
  focusReadOnly,
  declarationsReadOnly,
  declarationByPriorityId,
  declBusy,
  onSaveDeclaration,
  onClearDeclaration,
  onCreate,
  onEdit,
  onArchive,
  onUnarchive,
}: TeamDirectionCardProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [showArchived, setShowArchived] = useState(false);
  // Compact-card accordion (mobile density). Each active direction renders as a
  // COLLAPSED summary by default; only the summary + status badges + authority
  // actions show until the human expands it. ONE-OPEN across all viewports:
  // opening a card yields a set containing ONLY that id, so whichever card was
  // open collapses — the page never returns to a wall of expanded rows. Local
  // choice, no persistence, and never auto-opened by focus / blocker / position
  // (expansion is the user's). A Set makes the one-open rule explicit.
  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set());
  const toggleExpanded = (id: number) =>
    setOpenIds(prev => new Set(prev.has(id) ? [] : [id]));
  // Phase W2 — declaration editor state. STATUS STARTS UNSET (null): the
  // human's explicit pick is the only thing that ever selects one — no
  // default, no suggestion, no copy from lifecycle/focus/last week.
  const [declTarget, setDeclTarget] = useState<TeamDirectionRow | null>(null);
  const [declStatus, setDeclStatus] = useState<DeclarationStatus | null>(null);
  const [declNote, setDeclNote] = useState("");
  const [declBlocker, setDeclBlocker] = useState("");
  const [declNextStep, setDeclNextStep] = useState("");
  const [declDecision, setDeclDecision] = useState("");
  const [declConfirmClear, setDeclConfirmClear] = useState(false);
  const openDeclEditor = (p: TeamDirectionRow) => {
    const existing = declarationByPriorityId.get(p.id) ?? null;
    // Editing an EXISTING declaration seeds the human's own prior choice;
    // a NEW declaration always starts with status null (unset).
    setDeclStatus(existing ? (existing.status as DeclarationStatus) : null);
    setDeclNote(existing?.note ?? "");
    setDeclBlocker(existing?.blocker ?? "");
    setDeclNextStep(existing?.nextStep ?? "");
    setDeclDecision(existing?.decisionRequired ?? "");
    setDeclConfirmClear(false);
    setDeclTarget(p);
  };
  // Mobile-polish preview: the ACTIVE list shows the first 3 by default so the
  // page isn't a wall of cards before Team Pulse. The "View all"/"Show less"
  // affordance is COUNT-FREE and STATE-INVARIANT — its text/styling are the
  // same whether 4 or 40 priorities are hidden (never "View all 8"/"3 of 8").
  const [showAllActive, setShowAllActive] = useState(false);
  const ACTIVE_PREVIEW = 3;

  // Split on the SHARED team field `teamStatus` only — never on links. Anything
  // not "active" (archived, and the never-set-in-UI "done") lives in history.
  // Phase W2 ordering: FOCUSED directions first (stable within each group) —
  // visual priority for this week's attention, never a ranking or a score.
  const active = priorities
    .filter(p => p.teamStatus === "active")
    .sort((a, b) => Number(focusedIds.has(b.id)) - Number(focusedIds.has(a.id)));
  const archived = priorities.filter(p => p.teamStatus !== "active");
  const isEmpty = priorities.length === 0;
  // Stage 2 — Weekly Focus is shown on ACTIVE rows only. The empty state keys
  // off whether any ACTIVE direction is focused this week (a focus left on a
  // since-archived row must not hide the "no focus" state).
  const activeFocusedCount = active.filter(p => focusedIds.has(p.id)).length;
  // Preview/collapse applies ONLY to active; archived stays in its own section.
  const visibleActive = showAllActive ? active : active.slice(0, ACTIVE_PREVIEW);
  const hasMoreActive = active.length > ACTIVE_PREVIEW;

  return (
    <div
      data-testid="team-direction-card"
      className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="min-w-0">
          <h2 className="font-display text-lg">{t("Team direction", "توجيه الفريق")}</h2>
        </div>
        {canEdit ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            data-testid="td-new"
            onClick={onCreate}
          >
            <Plus className="h-4 w-4 me-1" /> {t("New priority", "أولوية جديدة")}
          </Button>
        ) : null}
      </div>
      {/* GLOSSARY (terminology cleanup): "relevant" = the Stage 2A binary
          private mark (a bookmark; no personal priority attached).
          "connected" = a Stage 3 REAL personal-priority link — that language
          lives only in the My-work panel and the My-priorities tags.
          ONE helper line (final-UX-polish pass): the old header subtitle
          repeated "Shared priorities" — merged here, said once. */}
      <p className="text-xs text-muted-foreground mb-4" data-testid="td-subtitle">
        {t(
          "Shared priorities for this team. Marking relevance is private. Only you can see what you've marked as relevant.",
          "أولويات مُشترَكة لهذا الفريق. تحديد الصلة خاص. أنت وحدك ترى ما حدّدته بأنه يهمّك.",
        )}
      </p>

      {isEmpty ? (
        <div
          data-testid="td-empty"
          className="text-sm text-muted-foreground py-4 text-center"
        >
          {t("No team priorities yet.", "لا أولويات للفريق بعد.")}
        </div>
      ) : (
        <>
          {/* Curation hint (Stage 1 weekly operating model) — direction now
              persists across weeks, so near-duplicates from earlier weeks can
              surface together. Guide authorized editors to CURATE by archive
              (never a scripted dedupe, never a hard delete). Count-free and
              shown only to editors when there is active direction to curate. */}
          {canEdit && active.length > 0 ? (
            <p
              data-testid="td-curation-hint"
              className="text-[11px] text-muted-foreground -mt-2 mb-4"
            >
              {t(
                "Team direction stays across weeks. If this is a duplicate from an earlier week, archive the old copy.",
                "يبقى توجيه الفريق ظاهرًا عبر الأسابيع. إذا كان هذا نسخة مكررة من أسبوع سابق، فأرشِف النسخة القديمة.",
              )}
            </p>
          ) : null}
          {/* Weekly Focus empty state (Stage 2) — count-free, shown to all when
              there is active direction but nothing is focused for the selected
              week. Focus CHANGES by week; the direction list above does not. */}
          {!focusReadOnly && active.length > 0 && activeFocusedCount === 0 ? (
            <p
              data-testid="weekly-focus-empty"
              className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5"
            >
              <Star className="h-3.5 w-3.5" />
              {t("No weekly focus set for this week.", "لا يوجد تركيز محدَّد لهذا الأسبوع.")}
            </p>
          ) : null}
          {/* Active direction. Members view + privately link; authorized roles
              also edit + archive. */}
          {active.length === 0 ? (
            <div
              data-testid="td-active-empty"
              className="text-sm text-muted-foreground py-3 text-center"
            >
              {t("No active team priorities.", "لا أولويات نشطة للفريق.")}
            </div>
          ) : (
            <ul className="space-y-3">
              {visibleActive.map(p => {
                const linked = linkedIds.has(p.id);
                const busy = busyLinkId === p.id;
                const focused = focusedIds.has(p.id);
                const focusNote = focusNoteById.get(p.id) ?? null;
                const focusBusy = busyFocusId === p.id;
                const decl = declarationByPriorityId.get(p.id) ?? null;
                const isOpen = openIds.has(p.id);
                const detailsId = `td-details-${p.id}`;
                return (
                  <li
                    key={p.id}
                    data-testid={`td-item-${p.id}`}
                    className="rounded-2xl border border-border/50 p-3"
                  >
                    {/* COLLAPSED SUMMARY (always visible) — density-first. Only
                        the title, small metadata, the shared focus/status
                        badges and (for authority) edit/archive. Description,
                        declaration detail, private My work + relevance live
                        behind "View details". Target ~110–150px tall. */}
                    <div className="flex items-start justify-between gap-2">
                      {/* Row-tap expander. Contains ONLY phrasing content
                          (spans/Badges) so it is valid inside a <button>;
                          edit/archive stay OUTSIDE it and never toggle it. */}
                      <button
                        type="button"
                        data-testid={`td-summary-${p.id}`}
                        aria-expanded={isOpen}
                        aria-controls={detailsId}
                        onClick={() => toggleExpanded(p.id)}
                        className="min-w-0 flex-1 text-start rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      >
                        <span className="block font-medium break-words line-clamp-2">
                          {p.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 flex-wrap">
                          {/* team/shared label — team rows must never look
                              like private personal rows (workspace clarity) */}
                          <Badge
                            variant="outline"
                            className="text-[10px]"
                            data-testid={`td-shared-${p.id}`}
                          >
                            {t("Team", "الفريق")}
                          </Badge>
                          <Badge
                            variant="secondary"
                            className={cn("text-[10px]", importanceToneClass(p.importance))}
                            title={isAr ? "الأهمية" : "Importance"}
                          >
                            {impLabel(p.importance, isAr)}
                          </Badge>
                          {p.youAuthored ? (
                            <span className="text-[10px] text-muted-foreground">
                              {t("you added this", "أضفتها أنت")}
                            </span>
                          ) : null}
                          {/* Focused-this-week summary marker (visible to ALL
                              members). The mark/unmark toggle + note live in the
                              expanded details, never here. */}
                          {focused ? (
                            <Badge
                              variant="secondary"
                              className={cn("text-[11px] py-1", FOCUS_MARKER_TONE)}
                              data-testid={`td-focus-marker-${p.id}`}
                            >
                              <Star className="h-3 w-3 me-1 fill-current" />
                              {t("Focused this week", "مُركَّز هذا الأسبوع")}
                            </Badge>
                          ) : null}
                          {/* Declared shared status summary badge (only when a
                              declaration exists — human-owned, never machine
                              written, focus-independent). The note/blocker/next
                              step/decision are in the expanded details. */}
                          {decl ? (
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-[11px] py-1",
                                DECL_STATUS_TONE[decl.status as DeclarationStatus],
                              )}
                              data-testid={`td-decl-status-${p.id}`}
                            >
                              {t(
                                DECL_STATUS_LABEL[decl.status as DeclarationStatus][0],
                                DECL_STATUS_LABEL[decl.status as DeclarationStatus][1],
                              )}
                            </Badge>
                          ) : null}
                        </span>
                      </button>
                      {canEdit ? (
                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 rounded-lg"
                            data-testid={`td-edit-${p.id}`}
                            aria-label={t("Edit", "تعديل")}
                            onClick={() => onEdit(p)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 rounded-lg"
                            data-testid={`td-archive-${p.id}`}
                            aria-label={t("Archive this priority", "أرشفة هذه الأولوية")}
                            onClick={() => onArchive(p.id)}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    {/* One clear expand affordance. Real <button> with the
                        priority title in its accessible label; chevron reflects
                        state. Members get this too (to read shared details). */}
                    <button
                      type="button"
                      data-testid={`td-details-toggle-${p.id}`}
                      aria-expanded={isOpen}
                      aria-controls={detailsId}
                      aria-label={
                        isOpen
                          ? t(`Hide details for ${p.title}`, `إخفاء تفاصيل ${p.title}`)
                          : t(`View details for ${p.title}`, `عرض تفاصيل ${p.title}`)
                      }
                      onClick={() => toggleExpanded(p.id)}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-coral rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                    >
                      {isOpen
                        ? t("Hide details", "إخفاء التفاصيل")
                        : t("View details", "عرض التفاصيل")}
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 transition-transform",
                          isOpen && "rotate-180",
                        )}
                      />
                    </button>

                    {/* EXPANDED DETAILS — description, focus editor/note, full
                        declaration detail, the owner-only private My work panel,
                        and the private relevance mark. Rendered ONLY when this
                        row is the open one (one-open accordion), so the
                        collapsed face carries no description and no private
                        linked-work data at all. */}
                    {isOpen ? (
                      <div
                        id={detailsId}
                        role="region"
                        aria-label={t(`Details for ${p.title}`, `تفاصيل ${p.title}`)}
                        data-testid={`td-details-${p.id}`}
                        className="mt-3 border-t border-border/40 pt-3"
                      >
                        {p.description ? (
                          <p
                            className="text-sm text-muted-foreground line-clamp-2"
                            data-testid={`td-desc-${p.id}`}
                          >
                            {p.description}
                          </p>
                        ) : null}

                        {/* Weekly Focus (Stage 2) — the shared marker lives in
                            the summary; here we expose the authority mark/unmark
                            action + the optional shared note. Purely shared data
                            — no counts, no who-focused. */}
                        {(focusReadOnly ? focused : focused || canEdit) ? (
                          <div className="mt-2" data-testid={`td-focus-${p.id}`}>
                            {/* Discoverability polish: the focus ACTION is a real
                                bordered pill BUTTON (h-8 mobile tap target),
                                never plain text. NEUTRAL "Remove focus" when set
                                (authority only; never destructive-red — unmarking
                                is reversible). Same handlers, same mutations,
                                same authority gating as before. */}
                            <div className="flex flex-wrap items-center gap-2">
                              {!focusReadOnly && canEdit ? (
                                focused ? (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={focusBusy}
                                    aria-pressed
                                    data-testid={`td-focus-toggle-${p.id}`}
                                    onClick={() => onToggleFocus(p.id, true)}
                                    className="h-8 rounded-full text-xs text-muted-foreground"
                                  >
                                    {t("Remove focus", "إزالة التركيز")}
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={focusBusy}
                                    aria-pressed={false}
                                    data-testid={`td-focus-toggle-${p.id}`}
                                    onClick={() => onToggleFocus(p.id, false)}
                                    className="h-8 rounded-full text-xs"
                                  >
                                    <Star className="h-3.5 w-3.5 me-1" />
                                    {t("Focus this week", "تركيز هذا الأسبوع")}
                                  </Button>
                                )
                              ) : null}
                            </div>
                            {/* Optional shared note (kept simple): authority
                                edits inline (saves on Enter/blur); members see it
                                read-only when present. */}
                            {!focusReadOnly && focused && canEdit ? (
                              <input
                                key={`fn-${p.id}-${focusNote ?? ""}`}
                                defaultValue={focusNote ?? ""}
                                maxLength={280}
                                placeholder={t(
                                  "Add a short focus note (optional)",
                                  "أضف ملاحظة تركيز قصيرة (اختياري)",
                                )}
                                data-testid={`td-focus-note-input-${p.id}`}
                                onKeyDown={e => {
                                  if (e.key === "Enter") {
                                    onSetFocusNote(p.id, e.currentTarget.value);
                                    e.currentTarget.blur();
                                  }
                                }}
                                onBlur={e => {
                                  const v = e.currentTarget.value;
                                  if ((v.trim() || null) !== (focusNote ?? null))
                                    onSetFocusNote(p.id, v);
                                }}
                                className="mt-1.5 w-full rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs"
                              />
                            ) : focused && focusNote ? (
                              <p
                                className="mt-1 min-w-0 text-xs text-muted-foreground break-words line-clamp-2"
                                data-testid={`td-focus-note-${p.id}`}
                              >
                                {focusNote}
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Phase W2 — this week's HUMAN-declared status detail
                            for this direction. The status badge itself is in the
                            summary; here we surface the shared note/blocker/next
                            step/decision and (authority only) the Add/Edit
                            affordance. Read-only on the Team page — declaring
                            lives in the Weekly Report wizard. Status is never
                            machine-written (no default/suggestion/carry-forward). */}
                        {decl || canEdit ? (
                          <div className="mt-2" data-testid={`td-decl-${p.id}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                {t("This week's status", "حالة هذا الأسبوع")}
                              </span>
                              {!decl ? (
                                <span
                                  className="text-xs text-muted-foreground"
                                  data-testid={`td-decl-none-${p.id}`}
                                >
                                  {t("No status declared", "لم تُعلَن حالة")}
                                </span>
                              ) : null}
                              {!declarationsReadOnly && canEdit ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={declBusy}
                                  className="h-8 rounded-full text-xs"
                                  data-testid={`td-decl-edit-${p.id}`}
                                  onClick={() => openDeclEditor(p)}
                                >
                                  {decl
                                    ? t("Edit status", "تعديل الحالة")
                                    : t("Add status", "إضافة حالة")}
                                </Button>
                              ) : null}
                            </div>
                            {/* §8 — the chain is Direction, Focus, Progress,
                                Blocker, Next. `note` IS the progress step, but it
                                was the only one of the four rendered with no
                                name, so the middle link of the chain read as an
                                unattributed sentence. Labelled like its siblings. */}
                            {decl?.note ? (
                              <p
                                className="mt-1 min-w-0 text-xs text-muted-foreground break-words line-clamp-2"
                                data-testid={`td-decl-note-${p.id}`}
                              >
                                {t("Progress", "التقدم")}: {decl.note}
                              </p>
                            ) : null}
                            {decl?.blocker ? (
                              <p className="mt-0.5 min-w-0 text-xs text-muted-foreground break-words">
                                {t("Blocker", "عائق")}: {decl.blocker}
                              </p>
                            ) : null}
                            {decl?.nextStep ? (
                              <p className="mt-0.5 min-w-0 text-xs text-muted-foreground break-words">
                                {t("Next step", "الخطوة التالية")}: {decl.nextStep}
                              </p>
                            ) : null}
                            {decl?.decisionRequired ? (
                              <p className="mt-0.5 min-w-0 text-xs text-muted-foreground break-words">
                                {t("Decision required", "قرار مطلوب")}: {decl.decisionRequired}
                              </p>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Stage 3C — owner-only private linked work. Mounts on
                            ACTIVE rows only (archived rows carry ZERO Stage 3 UI,
                            per Step B link-agnosticism). Its own collapsed face
                            is state-invariant and it fetches nothing until the
                            owner expands it — and now the whole panel only mounts
                            once the row's details are open, so a collapsed row
                            reads nothing private at all. */}
                        <MyWorkPanel
                          teamPriorityId={p.id}
                          weekStartKey={weekStartKey}
                          isAr={isAr}
                        />

                        {/* Stage 2A binary mark — a small private relevance
                            BOOKMARK below the primary My-work panel. Legible
                            ([11px], full muted color) but ghost/text styling only
                            — never a Button/pill/filled CTA (style-contract
                            tested). No personal priority is attached; same toggle
                            as always (priorityLinks.link/unlink).
                            SEMANTICS (contract-tested in teamPulse privacy tests):
                            "Relevant to me" is a private bookmark, NOT progress —
                            it must NEVER feed Team Pulse, dashboards, statistics,
                            or counts. Team Pulse reads approved-receipt signals
                            only and never touches priorityLinks /
                            personalPriorityLinks / personalPriorities. */}
                        <div className="mt-1.5 pb-1 min-h-6 flex items-center gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            data-testid={`td-link-${p.id}`}
                            aria-pressed={linked}
                            onClick={() => onToggleLink(p.id, linked)}
                            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                          >
                            {linked ? (
                              <BookmarkCheck className="h-3.5 w-3.5" />
                            ) : (
                              <Bookmark className="h-3.5 w-3.5" />
                            )}
                            {linked
                              ? t("Relevant to me", "تهمّني")
                              : t("Mark as relevant to me", "حدِّدها بأنها تهمّني")}
                          </button>
                          <span className="text-[11px] text-muted-foreground">
                            {linked
                              ? t("Only you can see this.", "أنت وحدك ترى هذا.")
                              : t("Private to you.", "خاص بك.")}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {/* COUNT-FREE, state-invariant preview toggle for ACTIVE priorities.
              Appears only when >3 active exist (the visible list already
              reveals that more than 3 exist); the LABEL and styling never
              vary by the hidden count — no "View all 8" / "3 of 8" / "+N more".
              Archived rows are NOT part of this — they have their own section. */}
          {hasMoreActive ? (
            <button
              type="button"
              data-testid="td-active-toggle"
              onClick={() => setShowAllActive(s => !s)}
              aria-expanded={showAllActive}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
            >
              {showAllActive ? t("Show less", "عرض أقل") : t("View all", "عرض الكل")}
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  showAllActive && "rotate-180",
                )}
              />
            </button>
          ) : null}

          {/* Archived history — separated + collapsed behind a neutral toggle.
              All members may VIEW; only authorized roles get Unarchive. NO link
              UI here at all (history isn't linked against), so archived rows
              expose no link state and offer no link/count oracle. */}
          {archived.length > 0 ? (
            <div
              data-testid="td-archived"
              className="mt-5 border-t border-border/50 pt-4"
            >
              <button
                type="button"
                data-testid="td-archived-toggle"
                onClick={() => setShowArchived(s => !s)}
                aria-expanded={showArchived}
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    showArchived && "rotate-180",
                  )}
                />
                {showArchived
                  ? t("Hide archived priorities", "إخفاء الأولويات المؤرشَفة")
                  : t("Show archived priorities", "عرض الأولويات المؤرشَفة")}
              </button>

              {showArchived ? (
                <div data-testid="td-archived-section" className="mt-3">
                  <p className="text-[11px] text-muted-foreground mb-3">
                    {t(
                      "Archived priorities are hidden from active team direction. This preserves private organization history.",
                      "الأولويات المؤرشَفة مخفية عن توجيه الفريق النشط. هذا يحفظ سجلّ التنظيم الخاص.",
                    )}
                  </p>
                  <ul className="space-y-2">
                    {archived.map(p => (
                      <li
                        key={p.id}
                        data-testid={`td-archived-item-${p.id}`}
                        className="rounded-2xl border border-border/40 bg-muted/20 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-muted-foreground">
                                {p.title}
                              </span>
                              <Badge
                                variant="secondary"
                                className={cn("text-[10px]", importanceToneClass(p.importance))}
                                title={isAr ? "الأهمية" : "Importance"}
                              >
                                {impLabel(p.importance, isAr)}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">
                                {statusLabel(p.teamStatus, isAr)}
                              </Badge>
                              {p.youAuthored ? (
                                <span className="text-[10px] text-muted-foreground">
                                  {t("you added this", "أضفتها أنت")}
                                </span>
                              ) : null}
                            </div>
                            {p.description ? (
                              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                                {p.description}
                              </p>
                            ) : null}
                          </div>
                          {canEdit ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full shrink-0"
                              data-testid={`td-unarchive-${p.id}`}
                              aria-label={t(
                                "Unarchive this priority",
                                "إلغاء أرشفة هذه الأولوية",
                              )}
                              onClick={() => onUnarchive(p.id)}
                            >
                              <ArchiveRestore className="h-3.5 w-3.5 me-1" />{" "}
                              {t("Unarchive", "إلغاء الأرشفة")}
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {/* Phase W2 — declaration editor. HUMAN-owned: status begins unset for
          a new declaration (nothing preselected, nothing suggested); Save is
          disabled until the human explicitly picks one. Clearing meaningful
          content asks first. */}
      <Dialog
        open={!!declTarget}
        onOpenChange={o => {
          if (!o) setDeclTarget(null);
        }}
      >
        <DialogContent data-testid="td-decl-editor">
          <DialogHeader>
            <DialogTitle className="break-words">
              {t("This week's status", "حالة هذا الأسبوع")}
              {declTarget ? ` · ${declTarget.title}` : ""}
            </DialogTitle>
            <DialogDescription>
              {t(
                "Select the team's status for this priority and add optional shared context for the selected week.",
                "اختر حالة الفريق لهذه الأولوية وأضف سياقًا مُشارَكًا اختياريًا للأسبوع المحدد.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label={t("Status", "الحالة")}
          >
            {(Object.keys(DECL_STATUS_LABEL) as DeclarationStatus[]).map(s => (
              <Button
                key={s}
                size="sm"
                variant={declStatus === s ? "secondary" : "outline"}
                aria-pressed={declStatus === s}
                className={cn(
                  "h-8 rounded-full text-xs",
                  declStatus === s ? DECL_STATUS_TONE[s] : undefined,
                )}
                data-testid={`td-decl-pill-${s}`}
                onClick={() => setDeclStatus(s)}
              >
                {t(DECL_STATUS_LABEL[s][0], DECL_STATUS_LABEL[s][1])}
              </Button>
            ))}
          </div>
          <input
            value={declNote}
            maxLength={280}
            onChange={e => setDeclNote(e.target.value)}
            placeholder={t(
              "Optional note: a short factual description of the week's progress",
              "ملاحظة اختيارية: وصف واقعي قصير لتقدم الأسبوع",
            )}
            data-testid="td-decl-note-input"
            className="w-full rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs"
          />
          <Textarea
            value={declBlocker}
            maxLength={2000}
            onChange={e => setDeclBlocker(e.target.value)}
            placeholder={t(
              "Optional blocker: a shared blocker affecting this priority",
              "عائق اختياري: عائق مُشارَك يؤثر على هذه الأولوية",
            )}
            data-testid="td-decl-blocker-input"
            className="min-h-[52px] text-xs"
          />
          <Textarea
            value={declNextStep}
            maxLength={2000}
            onChange={e => setDeclNextStep(e.target.value)}
            placeholder={t(
              "Optional next step: the next intended shared action",
              "خطوة تالية اختيارية: الإجراء المُشارَك المقصود التالي",
            )}
            data-testid="td-decl-nextstep-input"
            className="min-h-[52px] text-xs"
          />
          <Textarea
            value={declDecision}
            maxLength={2000}
            onChange={e => setDeclDecision(e.target.value)}
            placeholder={t(
              "Optional decision required: a decision or support request needing attention",
              "قرار مطلوب اختياري: قرار أو طلب دعم يحتاج اهتمامًا",
            )}
            data-testid="td-decl-decision-input"
            className="min-h-[52px] text-xs"
          />
          <p className="text-[11px] text-muted-foreground" data-testid="td-decl-trust">
            {t(
              "This is shared with team members. Status is declared by an authorised team lead or manager and is not calculated by AI.",
              "هذا مُشارَك مع أعضاء الفريق. الحالة يُعلنها قائد فريق أو مدير مخوَّل، ولا يحسبها الذكاء الاصطناعي.",
            )}
          </p>
          {declConfirmClear ? (
            <div
              className="rounded-xl border border-border/60 bg-muted/40 p-3"
              data-testid="td-decl-clear-confirm"
            >
              <p className="text-xs text-muted-foreground mb-2">
                {t(
                  "Clear this declaration? Its shared status and context for this week will be removed.",
                  "مسح هذا الإعلان؟ ستُزال حالته وسياقه المُشارَكان لهذا الأسبوع.",
                )}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-full text-xs"
                  data-testid="td-decl-clear-go"
                  onClick={() => {
                    if (declTarget) onClearDeclaration(declTarget.id);
                    setDeclTarget(null);
                  }}
                >
                  {t("Clear", "مسح")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 rounded-full text-xs"
                  onClick={() => setDeclConfirmClear(false)}
                >
                  {t("Keep", "إبقاء")}
                </Button>
              </div>
            </div>
          ) : null}
          <DialogFooter className="mt-1 flex-wrap gap-2">
            {declTarget && declarationByPriorityId.get(declTarget.id) ? (
              <Button
                variant="ghost"
                className="text-muted-foreground me-auto"
                data-testid="td-decl-clear"
                onClick={() => {
                  const existing = declTarget
                    ? declarationByPriorityId.get(declTarget.id)
                    : null;
                  const meaningful = !!(
                    existing &&
                    (existing.note || existing.blocker || existing.nextStep || existing.decisionRequired)
                  );
                  if (meaningful) setDeclConfirmClear(true);
                  else if (declTarget) {
                    onClearDeclaration(declTarget.id);
                    setDeclTarget(null);
                  }
                }}
              >
                {t("Clear declaration", "مسح الإعلان")}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setDeclTarget(null)}>
              {t("Cancel", "إلغاء")}
            </Button>
            <Button
              disabled={declStatus == null || declBusy}
              data-testid="td-decl-save"
              onClick={() => {
                if (!declTarget || declStatus == null) return;
                onSaveDeclaration(declTarget.id, {
                  status: declStatus,
                  note: declNote.trim() || null,
                  blocker: declBlocker.trim() || null,
                  nextStep: declNextStep.trim() || null,
                  decisionRequired: declDecision.trim() || null,
                });
                setDeclTarget(null);
              }}
            >
              {t("Save declaration", "حفظ الإعلان")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
